import { XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { type FC, useState } from "react";

import { Button, RadioInput, Small } from "@fronterahealth/frontera-ui-components";

import { RuleItem } from "@pages/QASettings/QARuleEditor";
import { PackageType, type PackageTypeValue } from "@pages/QASettings/baseQaTypes";
import { ReportCategory, type ReportCategoryValue } from "@pages/QASettings/constants";
import { type ReportType } from "@pages/QASettings/qaRuleBlob";
import { type EditableRuleFields, REPORT_TYPES, toggleReportType } from "@pages/QASettings/qaRuleFields";
import { reportTypesFromCategory } from "@pages/QASettings/reportCategory";

// ---------------------------------------------------------------------------
// Shared field pieces (used by both the create form below and
// BaseQAPackageCard's inline edit form).
// ---------------------------------------------------------------------------

export const PackageTypeToggle: FC<{
  value: PackageTypeValue;
  onChange: (value: PackageTypeValue) => void;
}> = ({ value, onChange }) => (
  <div className="mb-4">
    <label className="mb-1 block text-xs font-medium text-text-secondary">Package type</label>
    <div className="inline-flex rounded-md border border-border-medium">
      {([PackageType.BASE, PackageType.INSURANCE] as PackageTypeValue[]).map((pt) => (
        <button
          key={pt}
          onClick={() => onChange(pt)}
          className={clsx(
            "px-4 py-1.5 text-sm capitalize first:rounded-l-md last:rounded-r-md",
            value === pt ? "bg-globemallow-600 text-white" : "bg-bg-default text-text-secondary hover:bg-bg-disabled",
          )}
        >
          {pt}
        </button>
      ))}
    </div>
  </div>
);

export const PayorTypeMultiSelect: FC<{
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
}> = ({ options, selected, onToggle }) => (
  <div className="mb-4">
    <label className="mb-1 block text-xs font-medium text-text-secondary">Payor types</label>
    <div className="flex flex-wrap gap-2">
      {options.map((value) => (
        <button
          key={value}
          onClick={() => onToggle(value)}
          className={clsx(
            "rounded-full border px-3 py-0.5 text-xs",
            selected.includes(value)
              ? "border-globemallow-600 bg-globemallow-600 text-white"
              : "border-border-medium bg-bg-default text-text-secondary hover:border-globemallow-600",
          )}
        >
          {value}
        </button>
      ))}
      {options.length === 0 && <Small colorType="secondary">No payor types available.</Small>}
    </div>
  </div>
);

const CATEGORY_ITEMS = [
  { id: ReportCategory.ABA, title: "ABA" },
  { id: ReportCategory.DIAGNOSIS, title: "Diagnosis" },
];

export const CategoryRadio: FC<{
  name: string;
  value: ReportCategoryValue;
  onChange: (value: ReportCategoryValue) => void;
}> = ({ name, value, onChange }) => (
  <div className="mb-4">
    <RadioInput
      title="Report category"
      legend="Whether this package applies to Diagnosis or ABA reports"
      name={name}
      items={CATEGORY_ITEMS}
      selectedId={value}
      onChange={(e) => onChange(e.target.value as ReportCategoryValue)}
    />
  </div>
);

// ---------------------------------------------------------------------------
// Create package form
// ---------------------------------------------------------------------------

// Draft rules use the same structured fields (name / IA-RA chips / location /
// what-to-scan-for / feedback) as the inline editor on already-created rules
// in BaseQAPackageCard — held here as plain field objects (no rule id yet)
// and serialized via serializeRuleBody at the createRule call site once the
// package exists (see BaseQAManager's handleCreatePackage).
export interface CreatePackageFormData {
  name: string;
  description: string;
  instructions: string;
  package_type: PackageTypeValue;
  payor_types: string[];
  report_types: string[];
  rules: EditableRuleFields[];
}

const BLANK_DRAFT_RULE: EditableRuleFields = {
  rule: "",
  types: [...REPORT_TYPES],
  location: "",
  scanFor: "",
  feedback: "",
};

interface BaseQAPackageFormProps {
  payorTypes: string[];
  onCancel: () => void;
  onSubmit: (data: CreatePackageFormData) => void | Promise<void>;
  isPending: boolean;
}

export const BaseQAPackageForm: FC<BaseQAPackageFormProps> = ({ payorTypes, onCancel, onSubmit, isPending }) => {
  const [packageType, setPackageType] = useState<PackageTypeValue>(PackageType.BASE);
  const [selectedPayorTypes, setSelectedPayorTypes] = useState<string[]>([]);
  const [category, setCategory] = useState<ReportCategoryValue>(ReportCategory.ABA);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");
  const [rules, setRules] = useState<EditableRuleFields[]>([]);
  const [expandedRuleIndexes, setExpandedRuleIndexes] = useState<Set<number>>(() => new Set());

  const togglePayorType = (value: string) => {
    setSelectedPayorTypes((prev) => (prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value]));
  };

  // Mirrors BaseQAPackageCard's "+ Add rule": append a blank structured rule
  // and immediately expand it so the editor is visible for filling in.
  const handleAddRule = () => {
    const index = rules.length;
    setRules((prev) => [...prev, { ...BLANK_DRAFT_RULE }]);
    setExpandedRuleIndexes((s) => new Set(s).add(index));
  };

  const updateRule = (index: number, patch: Partial<EditableRuleFields>) =>
    setRules((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const toggleRuleType = (index: number, t: ReportType) =>
    setRules((prev) => prev.map((r, i) => (i === index ? { ...r, types: toggleReportType(r.types, t) } : r)));

  const removeRule = (index: number) => {
    setRules((prev) => prev.filter((_, i) => i !== index));
    setExpandedRuleIndexes((s) => {
      const next = new Set<number>();
      s.forEach((i) => {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      });
      return next;
    });
  };

  const toggleRuleExpand = (index: number) =>
    setExpandedRuleIndexes((s) => {
      const next = new Set(s);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  const handleSubmit = async () => {
    if (!name.trim()) return;
    await onSubmit({
      name: name.trim(),
      description: description.trim(),
      instructions: instructions.trim(),
      package_type: packageType,
      payor_types: selectedPayorTypes,
      report_types: reportTypesFromCategory(category),
      rules,
    });
  };

  return (
    <div className="mt-4 w-full rounded-lg border border-border-medium bg-bg-surface p-6">
      <div className="mb-4 flex items-center justify-between">
        <Small colorType="primary">New package</Small>
        <button onClick={onCancel} className="text-text-tertiary hover:text-text-primary">
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      <PackageTypeToggle value={packageType} onChange={setPackageType} />

      {packageType === PackageType.INSURANCE && (
        <PayorTypeMultiSelect options={payorTypes} selected={selectedPayorTypes} onToggle={togglePayorType} />
      )}

      <CategoryRadio name="new-package-category" value={category} onChange={setCategory} />

      {/* Name */}
      <div className="mb-3">
        <label className="mb-1 block text-xs font-medium text-text-secondary">
          Name <span className="font-normal text-text-tertiary">— visible to users</span>
        </label>
        <input
          className="w-full rounded border border-border-medium bg-bg-default px-3 py-1.5 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-globemallow-600"
          placeholder="e.g., Commercial Standard"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      {/* Description */}
      <div className="mb-3">
        <label className="mb-1 block text-xs font-medium text-text-secondary">
          Description <span className="font-normal text-text-tertiary">— visible to users</span>
        </label>
        <input
          className="w-full rounded border border-border-medium bg-bg-default px-3 py-1.5 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-globemallow-600"
          placeholder="Short description shown to users"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      {/* Instructions */}
      <div className="mb-4">
        <label className="mb-1 block text-xs font-medium text-text-secondary">
          Instructions{" "}
          <span className="font-normal text-text-tertiary">
            (optional, LLM only) — use for guidance that doesn&apos;t fit a yes/no rule
          </span>
        </label>
        <textarea
          className="w-full rounded border border-border-medium bg-bg-default px-3 py-1.5 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-globemallow-600"
          placeholder="Optional. Leave blank if all guidance fits into individual rules below."
          rows={3}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
        />
      </div>

      {/* Rules — same structured editor (name / IA-RA chips / location /
          what-to-scan-for / feedback) used on already-created rules in
          BaseQAPackageCard; held as plain field objects here since there's
          no rule id until the package is created. */}
      <div className="mb-4">
        <label className="mb-2 block text-xs font-medium text-text-secondary">Rules</label>
        {rules.length > 0 && (
          <div className="mb-2 overflow-hidden rounded border border-border-subtle">
            {rules.map((fields, i) => (
              <RuleItem
                key={i}
                fields={fields}
                expanded={expandedRuleIndexes.has(i)}
                handlers={{
                  onToggleExpand: () => toggleRuleExpand(i),
                  onChangeField: (patch) => updateRule(i, patch),
                  onToggleType: (t) => toggleRuleType(i, t),
                  onDelete: () => removeRule(i),
                  onBlurSave: () => {},
                }}
              />
            ))}
          </div>
        )}
        <button
          onClick={handleAddRule}
          className="flex items-center gap-1 rounded border border-globemallow-600 px-3 py-1.5 text-sm font-medium text-globemallow-600 hover:bg-globemallow-50"
        >
          + Add rule
        </button>
      </div>

      <div className="flex gap-2">
        <Button appearance="primary" text={isPending ? "Creating…" : "Create package"} onClick={handleSubmit} />
        <Button appearance="secondary" text="Cancel" onClick={onCancel} />
      </div>
    </div>
  );
};
